import type { ReactNode, RefObject } from 'react'
import { createContext, useContext, useState } from 'react'

type DialogPortalContextType = {
  dialogRef: RefObject<HTMLElement | null> | null
  setDialogRef: (ref: RefObject<HTMLElement | null> | null) => void
}

const DialogPortalContext = createContext<DialogPortalContextType | undefined>(undefined)

/**
 * Provides a react friendly reference to the currently opened dialog
 * Note that it only provides such a thing, if the opening/closing dialog manages the ref correctly.
 *
 * This is needed for components such as react-select Selects or tooltips to be rendered into the correct context
 *
 * @param children
 * @constructor
 */
export const DialogPortalProvider = ({ children }: { children: ReactNode }) => {
  const [dialogRef, setDialogRef] = useState<RefObject<HTMLElement | null> | null>(null)

  return (
    <DialogPortalContext.Provider value={{ dialogRef, setDialogRef }}>
      {children}
    </DialogPortalContext.Provider>
  )
}

export const useDialogPortal = (): DialogPortalContextType => {
  const ctx = useContext(DialogPortalContext)
  if (!ctx) throw new Error('useDialogPortal must be used within a DialogPortalProvider')
  return ctx
}
